/*
 * スイッチ管理タイプ共通｜商品一覧の絞り込みエンジン
 *
 * 対象: 61 アンマネージド / 62 イージースマート / 63 スマート /
 *       smart-cloud-switches スマートクラウド / 75 フルマネージ
 *
 * 従来はカテゴリーごとに unmanaged-filter.js / easy-smart-filter.js /
 * smart-cloud-filter.js / full-managed-filter.js / smart-switch-port-nav.js の
 * 5本を個別に持っていた（合計約1,800行、うち実質同一が9割）。
 * それらを1本に統合し、カテゴリー差は「Liquidが出力するDOMの data 属性」
 * だけで吸収する。JS側にカテゴリー名も軸の一覧もタグの一覧も持たない。
 *
 * ---- 設計の骨子 ----
 *
 * 1. 単一の内部 state と単一の matchesProduct() だけを正とする。
 *    商品カードの表示／ボタン件数／active状態／「◯個の商品」／empty state は
 *    すべて render() から導出する。fetch・Section Rendering API は使わない
 *    （5カテゴリとも全商品が1ページに描画済みで、ページネーションが無い）。
 *
 * 2. 軸の一覧は data-swf-axes から読む。軸が3本でも4本でもロジックは変わらない。
 *
 * 3. 「URLに出る値」と「商品タグ」を分離する。
 *      data-swf-value … URLのクエリ値（例: 75のレンジキー "m"）
 *      data-swf-tags  … 実際に突き合わせる商品タグ（例: "ports-16,ports-24,ports-26"）
 *    value を省略した場合は tags をそのまま value として使う
 *    （61/62/SC のように "ports-8" がそのままURLに出るケース）。
 *    この分離により、75のレンジ対応表をJS側に二重で持つ必要が無くなり、
 *    かつ既存URL（?ports=m）の互換性も保たれる。
 *
 * 4. tags の書式
 *      空文字      … その軸の「すべて」（無条件）
 *      'none'      … 否定条件。poe-supported を持たない＝PoE非対応。
 *                    既存タグ体系に「PoE非対応」を表す肯定タグが無いため特別扱いする。
 *      'a,b,c'     … 複数タグのOR
 *      'a'         … 単一タグの完全一致
 *
 * 5. 0件の選択肢は aria-disabled + tabindex="-1" + クリック抑止の3点セットで
 *    完全に無効化する（従来はカテゴリーごとにバラバラだった）。
 *
 * 6. Dawn標準 facets.js（並び替え・Availability・Price）が
 *    #ProductGridContainer を差し替えたときは MutationObserver で再同期し、
 *    このエンジンが管理するクエリキーだけをURLへ書き戻す（履歴は増やさない）。
 *
 * ---- Liquid 側が用意すべきDOM ----
 *
 *   <div id="swf-filter"
 *        data-swf-root
 *        data-swf-base-url="/collections/61"
 *        data-swf-axes="ports,poe,speed">
 *     <ul data-swf-group="ports">
 *       <li><a class="swf-filter__btn"
 *              data-swf-group="ports"
 *              data-swf-value=""        (すべて)
 *              data-swf-tags=""
 *              aria-pressed="true">すべて<span data-swf-count>（30）</span></a></li>
 *       ...
 *     </ul>
 *     <a data-swf-reset hidden>絞り込みを解除する</a>
 *   </div>
 *   <script type="application/json" id="swf-filter-data">[{"handle":..,"tags":[..]}]</script>
 */
(function () {
  'use strict';

  var supported =
    typeof window.URLSearchParams === 'function' &&
    !!(window.history && typeof window.history.pushState === 'function') &&
    typeof Element.prototype.closest === 'function';
  if (!supported) return;

  /**
   * 1つのフィルターUIを初期化する。
   * @param {HTMLElement} nav ルート要素（data-swf-root を持つ）
   */
  function createSwitchFilter(nav) {
    if (!nav || nav.dataset.swfInitialized === 'true') return;

    var basePath = nav.dataset.swfBaseUrl;
    if (!basePath) return;

    var AXES = (nav.dataset.swfAxes || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
    if (!AXES.length) return;

    var section = nav.closest('[id^="shopify-section-"]') || nav;

    // ---- 商品データ（handle と絞り込みに使うタグだけの写し） ----
    var filterData = null;
    var dataEl = document.getElementById(nav.dataset.swfDataId || 'swf-filter-data');
    if (dataEl) {
      try {
        filterData = JSON.parse(dataEl.textContent);
      } catch (e) {
        filterData = null;
      }
    }
    // データが壊れていたら絞り込み機能自体を諦める（商品一覧は全件表示のまま残る）
    if (!filterData || !filterData.length) return;

    nav.dataset.swfInitialized = 'true';

    function navLinks() {
      return Array.prototype.slice.call(nav.querySelectorAll('.swf-filter__btn'));
    }

    // ---- Liquidが描画したボタンから「軸ごとの許可値」と「値→タグ」の対応を集める。
    //      これによりタグ構成や選択肢の増減でこのJSを直す必要が無くなる。 ----
    var AXIS_VALUES = {}; // { ports: ['ports-8', ...] }
    var TAGS_FOR = {};    // { 'ports|m': 'ports-16,ports-24,ports-26' }
    AXES.forEach(function (axis) { AXIS_VALUES[axis] = []; });

    function valueOf(a) {
      // data-swf-value が無ければ data-swf-tags をそのまま値として使う
      var v = a.getAttribute('data-swf-value');
      if (v === null) v = a.getAttribute('data-swf-tags') || '';
      return v;
    }

    navLinks().forEach(function (a) {
      var group = a.getAttribute('data-swf-group');
      if (!AXIS_VALUES[group]) return;
      var value = valueOf(a);
      if (!value) return; // 「すべて」は空文字なので登録しない
      if (AXIS_VALUES[group].indexOf(value) === -1) AXIS_VALUES[group].push(value);
      TAGS_FOR[group + '|' + value] = a.getAttribute('data-swf-tags') || '';
    });

    // ---- 判定関数（カード表示・件数計算・empty state判定が共通で使う唯一の実装） ----
    function matchesTags(tags, wantedSpec) {
      if (!wantedSpec) return true;
      if (wantedSpec === 'none') return tags.indexOf('poe-supported') === -1;
      var wanted = wantedSpec.split(',');
      for (var i = 0; i < wanted.length; i++) {
        if (tags.indexOf(wanted[i].trim()) !== -1) return true;
      }
      return false;
    }

    function matchesAxis(tags, axis, value) {
      if (!value) return true;
      return matchesTags(tags, TAGS_FOR[axis + '|' + value]);
    }

    function matchesProduct(tags, st) {
      for (var i = 0; i < AXES.length; i++) {
        if (!matchesAxis(tags, AXES[i], st[AXES[i]])) return false;
      }
      return true;
    }

    // 「自分の軸だけ候補へ差し替え、他軸は現在stateを維持」した件数（facet件数のルール）
    function countFor(axis, value) {
      var candidate = stateCopy();
      candidate[axis] = value;
      var n = 0;
      for (var i = 0; i < filterData.length; i++) {
        if (matchesProduct(filterData[i].tags, candidate)) n++;
      }
      return n;
    }

    // ---- URL ⇔ state ----
    function stateCopy() {
      var copy = {};
      AXES.forEach(function (axis) { copy[axis] = state[axis]; });
      return copy;
    }

    function emptyState() {
      var st = {};
      AXES.forEach(function (axis) { st[axis] = ''; });
      return st;
    }

    function stateFromUrl() {
      var params = new URLSearchParams(window.location.search);
      var next = {};
      AXES.forEach(function (axis) {
        var v = params.get(axis) || '';
        // Liquidが描画していない値（手打ちURL・古いブックマーク）は「すべて」に落とす
        next[axis] = AXIS_VALUES[axis].indexOf(v) !== -1 ? v : '';
      });
      return next;
    }

    function urlFor(st) {
      var params = new URLSearchParams();
      AXES.forEach(function (axis) {
        if (st[axis]) params.set(axis, st[axis]);
      });
      var qs = params.toString();
      return basePath + (qs ? '?' + qs : '');
    }

    // 現在のURLに残っている他パラメータ（sort_by・filter.v.* 等、Dawn標準facetsが
    // 管理するもの）はそのまま残し、このエンジンが持つ軸キーだけを上書き・削除する。
    function paramsWithCurrentState() {
      var params = new URLSearchParams(window.location.search);
      AXES.forEach(function (axis) {
        if (state[axis]) params.set(axis, state[axis]);
        else params.delete(axis);
      });
      return params;
    }

    var state = stateFromUrl();

    // ---- DOM操作 ----
    function gridItems() {
      var container = document.getElementById('ProductGridContainer');
      if (!container) return [];
      return Array.prototype.slice.call(container.querySelectorAll('.custom-product-grid__item'));
    }

    function handleFromItem(item) {
      var link = item.querySelector('a[href*="/products/"]');
      if (!link) return null;
      var href = link.getAttribute('href') || '';
      var m = href.match(/\/products\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    }

    function updateProductCount(count) {
      var text = count + '個の商品';
      ['ProductCount', 'ProductCountDesktop'].forEach(function (id) {
        var el = document.getElementById(id);
        if (el) el.textContent = text;
      });
      var mobile = document.querySelector('.mobile-facets__count');
      if (mobile) mobile.textContent = text;
    }

    function toggleEmptyState(show, hasNativeItems) {
      var grid = document.getElementById('product-grid');
      var emptyMsg = document.querySelector('[data-swf-empty]');
      if (show && hasNativeItems) {
        if (!emptyMsg && grid) {
          emptyMsg = document.createElement('p');
          emptyMsg.setAttribute('data-swf-empty', '');
          emptyMsg.className = 'swf-filter__empty';
          emptyMsg.textContent = '条件に一致する商品がありません。条件を変更してください。';
          grid.insertAdjacentElement('afterend', emptyMsg);
        }
        if (emptyMsg) emptyMsg.hidden = false;
      } else if (emptyMsg) {
        emptyMsg.hidden = true;
      }
    }

    function syncNav() {
      navLinks().forEach(function (a) {
        var group = a.getAttribute('data-swf-group');
        if (!group || !AXIS_VALUES[group]) return;
        var value = valueOf(a);

        var candidate = stateCopy();
        candidate[group] = value;
        a.setAttribute('href', urlFor(candidate));

        var active = state[group] === value;
        a.classList.toggle('swf-filter__btn--active', active);
        a.setAttribute('aria-pressed', active ? 'true' : 'false');

        var n = countFor(group, value);
        var countEl = a.querySelector('[data-swf-count]');
        if (countEl) countEl.textContent = '（' + n + '）';

        // 0件の候補は押しても空の結果にしかならないので、
        // 見た目・スクリーンリーダー・キーボード操作の三方向で無効にする
        // （クリックの抑止はクリックハンドラ側）。
        // 「すべて」と選択中のものは、絞り込みを解除する導線として常に有効に保つ。
        if (n === 0 && value !== '' && !active) {
          a.setAttribute('aria-disabled', 'true');
          a.setAttribute('tabindex', '-1');
        } else {
          a.removeAttribute('aria-disabled');
          a.removeAttribute('tabindex');
        }
      });

      // 「絞り込みを解除する」は、いずれかの軸が選択されているときだけ出す。
      var reset = nav.querySelector('[data-swf-reset]');
      if (reset) {
        reset.hidden = !anySelected();
        reset.setAttribute('href', basePath);
      }
    }

    function anySelected() {
      return AXES.some(function (axis) { return !!state[axis]; });
    }

    // ---- render(): 表示・件数・empty state・active状態をまとめて更新する唯一の入口。
    //      呼び出し元（クリック・popstate・facets.js後の再同期）を問わず常にここを通す。 ----
    function render() {
      section.setAttribute('aria-busy', 'true');

      var byHandle = {};
      filterData.forEach(function (item) { byHandle[item.handle] = item.tags; });

      var items = gridItems();
      var visibleCount = 0;
      items.forEach(function (item) {
        var handle = handleFromItem(item);
        var tags = handle ? byHandle[handle] : null;
        // 未知のhandle（データ不整合）は安全側として表示のままにする
        var show = !tags || matchesProduct(tags, state);
        item.hidden = !show;
        if (show) visibleCount++;
      });

      updateProductCount(visibleCount);
      toggleEmptyState(visibleCount === 0, items.length > 0);
      syncNav();

      section.setAttribute('aria-busy', 'false');
    }

    // クリック時の操作フィードバック。処理は同期的で一瞬のためspinnerは出さず、
    // 商品グリッドを短時間だけ薄くして「切り替わった」ことを伝える。
    var flashTimer = null;
    function flashGrid() {
      var gc = document.getElementById('ProductGridContainer');
      if (!gc) return;
      gc.setAttribute('aria-busy', 'true');
      gc.classList.add('swf-product-grid--filtering');
      if (flashTimer) clearTimeout(flashTimer);
      flashTimer = setTimeout(function () {
        gc.classList.remove('swf-product-grid--filtering');
        gc.setAttribute('aria-busy', 'false');
        flashTimer = null;
      }, 180);
    }

    function isPlainLeftClick(event) {
      return !(
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      );
    }

    function applyState(next) {
      state = next;
      // sort_by・filter.v.*（Dawn標準facetsの状態）が既にURLにあれば残したまま、
      // このエンジンが管理する軸キーだけを更新する。
      var qs = paramsWithCurrentState().toString();
      window.history.pushState({ swfFilter: true }, '', window.location.pathname + (qs ? '?' + qs : ''));
      render();
      flashGrid();
    }

    // ---- クリック操作 ----
    // 処理は同期的（fetchを伴わない）なので、クリック中に別のクリックが割り込む
    // 余地がなく、二重クリックによるstate破壊は構造的に発生しない。
    nav.addEventListener('click', function (event) {
      var reset = event.target.closest('[data-swf-reset]');
      if (reset && nav.contains(reset)) {
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        if (!anySelected()) return;
        applyState(emptyState());
        return;
      }

      var link = event.target.closest('.swf-filter__btn');
      if (!link || !nav.contains(link)) return;

      // 0件の候補は遷移させない。修飾キー付きクリック（新規タブ）も抑止する。
      if (link.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      if (!isPlainLeftClick(event)) return;
      event.preventDefault();

      var group = link.getAttribute('data-swf-group');
      if (!group || !AXIS_VALUES[group]) return;
      var value = valueOf(link);

      if (state[group] === value) return; // 変化なし
      var next = stateCopy();
      next[group] = value;
      applyState(next);
    });

    window.addEventListener('popstate', function () {
      state = stateFromUrl();
      render();
    });

    // ---- facets.js（Dawn標準の並び替え・Availability・Price）が
    //      #ProductGridContainer を独自に差し替えたときの再同期。
    //      既存のfacet完了イベントが無いため、唯一の検知手段として
    //      MutationObserver を1つだけ設置する。
    //      このエンジン自身は item.hidden と emptyMsg の初回挿入以外で
    //      ProductGridContainer 配下を書き換えず、emptyMsg 挿入は render() 内で
    //      冪等なため、マイクロタスク1回の再合流で収束し無限ループにならない。 ----
    var gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      var resyncScheduled = false;
      var observer = new MutationObserver(function () {
        if (resyncScheduled) return;
        resyncScheduled = true;
        Promise.resolve().then(function () {
          resyncScheduled = false;
          render();

          // facets.js は自分が管理するパラメータだけでURLを組み立て直すため、
          // このエンジンの軸キーが消えていることがある。表示・件数・ボタンは既に
          // stateどおり正しいが、URLだけが食い違うと
          // 「URL = stateのシリアライズ結果」が崩れるため、履歴を増やさずに書き戻す。
          var qs = paramsWithCurrentState().toString();
          var newSearch = qs ? '?' + qs : '';
          if (newSearch !== window.location.search) {
            window.history.replaceState(window.history.state, '', window.location.pathname + newSearch);
          }
        });
      });
      observer.observe(gridContainer, { childList: true, subtree: true });
    }

    // 初期表示（直接URLアクセス・リロード）でも、URLから復元したstateを即座に反映する。
    render();
  }

  /**
   * 旧方式（Shopify標準のタグパスURL）で来たアクセスをクエリ方式へ正規化する。
   *
   * collection 63 は以前 /collections/63/ports-24+uplink-sfp のような
   * タグパスURLで絞り込んでいた。この形式はShopifyがサーバー側で商品を絞るため
   * 商品表示自体は今も正しく動くが、フィルターUIはクエリ文字列を見るので
   * 「グリッドは2件・ボタンは全部すべて」という食い違いが起きる。
   *
   * そこで、タグパスに含まれる全タグが現在の選択肢に対応づけられる場合のみ、
   * 同じ意味のクエリURLへ置き換える（履歴を汚さないよう location.replace）。
   * 対応づけられないタグが1つでもあれば何もしない
   * （Shopify標準の絞り込み結果がそのまま表示される）。
   *
   * @param {HTMLElement} nav ルート要素
   * @returns {boolean} 置き換えを行ったら true
   */
  function normalizeLegacyTagPath(nav) {
    if (nav.dataset.swfLegacyTagPath !== 'true') return false;

    var basePath = nav.dataset.swfBaseUrl || '';
    var path = window.location.pathname.replace(/\/$/, '');
    var base = basePath.replace(/\/$/, '');
    if (path === base || path.indexOf(base + '/') !== 0) return false;

    var tagSegment = path.slice(base.length + 1);
    if (!tagSegment) return false;

    var tags = decodeURIComponent(tagSegment).split('+').filter(Boolean);
    if (!tags.length) return false;

    // 各タグがどの軸のどの値に当たるかをボタンから引く
    var map = {};
    Array.prototype.slice.call(nav.querySelectorAll('.swf-filter__btn')).forEach(function (a) {
      var spec = a.getAttribute('data-swf-tags') || '';
      if (!spec || spec === 'none') return;
      var value = a.getAttribute('data-swf-value');
      if (value === null) value = spec;
      var group = a.getAttribute('data-swf-group');
      spec.split(',').forEach(function (t) {
        map[t.trim()] = { group: group, value: value };
      });
    });

    var params = new URLSearchParams(window.location.search);
    for (var i = 0; i < tags.length; i++) {
      var hit = map[tags[i]];
      if (!hit) return false; // 1つでも解釈できなければ触らない
      params.set(hit.group, hit.value);
    }

    var qs = params.toString();
    window.location.replace(base + (qs ? '?' + qs : ''));
    return true;
  }

  function init() {
    var roots = Array.prototype.slice.call(document.querySelectorAll('[data-swf-root]'));
    for (var i = 0; i < roots.length; i++) {
      if (normalizeLegacyTagPath(roots[i])) return; // 遷移するので以降の初期化は不要
      createSwitchFilter(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopifyテーマエディタでセクションを編集・再読み込みしたときの再初期化
  document.addEventListener('shopify:section:load', init);
})();
